import { auth } from './auth.js';

// WS base resolution. Cases we need to cover:
//   - VITE_WS_BASE_URL=ws(s)://… → use verbatim
//   - VITE_WS_BASE_URL=http(s)://… → swap scheme to ws(s)
//   - VITE_WS_BASE_URL or VITE_API_BASE_URL is relative (e.g. "/api") →
//       The WebSocket ctor REQUIRES an absolute URL, so we derive scheme +
//       host from the current page and append the relative prefix.
//   - Nothing set → same-origin, no prefix.
function resolveWsBase() {
  const explicit = import.meta.env.VITE_WS_BASE_URL;
  const apiBase = import.meta.env.VITE_API_BASE_URL || '';
  if (explicit && /^https?:\/\//.test(explicit)) return explicit.replace(/^http/, 'ws');
  if (explicit && /^wss?:\/\//.test(explicit)) return explicit;
  const relative = explicit || apiBase || '';
  const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
  return `${proto}://${window.location.host}${relative}`;
}

const WS_BASE = resolveWsBase();

/**
 * Thin WebSocket wrapper with auto-reconnect, JSON messages, and event handlers.
 *
 *   const ws = new TrivianaSocket('/ws/match/123');
 *   ws.on('message', (m) => ...);
 *   ws.on('open', () => ws.send({ type: 'hello' }));
 *   ws.connect();
 */
export class TrivianaSocket {
  constructor(path, { autoReconnect = true, maxBackoffMs = 15000 } = {}) {
    this.path = path;
    this.autoReconnect = autoReconnect;
    this.maxBackoffMs = maxBackoffMs;
    this._handlers = { open: [], close: [], error: [], message: [] };
    this._ws = null;
    this._backoff = 500;
    this._closedByUser = false;
  }

  on(event, handler) {
    if (!this._handlers[event]) this._handlers[event] = [];
    this._handlers[event].push(handler);
    return this;
  }

  _emit(event, payload) {
    (this._handlers[event] || []).forEach((h) => {
      try { h(payload); } catch (e) { console.error('ws handler error', e); }
    });
  }

  connect() {
    this._closedByUser = false;
    const token = auth.getAccessToken();
    const sep = this.path.includes('?') ? '&' : '?';
    const url = `${WS_BASE}${this.path}${token ? `${sep}token=${encodeURIComponent(token)}` : ''}`;
    this._ws = new WebSocket(url);

    this._ws.addEventListener('open', () => {
      this._backoff = 500;
      this._emit('open');
    });
    this._ws.addEventListener('message', (e) => {
      let data = e.data;
      try { data = JSON.parse(e.data); } catch { /* keep as text */ }
      this._emit('message', data);
    });
    this._ws.addEventListener('error', (e) => this._emit('error', e));
    this._ws.addEventListener('close', (e) => {
      this._emit('close', e);
      if (this.autoReconnect && !this._closedByUser) {
        setTimeout(() => this.connect(), this._backoff);
        this._backoff = Math.min(this._backoff * 2, this.maxBackoffMs);
      }
    });
  }

  send(payload) {
    if (!this._ws || this._ws.readyState !== WebSocket.OPEN) return false;
    const data = typeof payload === 'string' ? payload : JSON.stringify(payload);
    this._ws.send(data);
    return true;
  }

  close() {
    this._closedByUser = true;
    if (this._ws) this._ws.close();
  }
}
